import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/helpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Check, X, Trash2, ChevronLeft, ChevronRight, Loader2, FileDown, AlertTriangle } from "lucide-react";

const MONTH_NAMES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const DAY_NAMES = ["L", "M", "X", "J", "V", "S", "D"];
const STATUS = {
  pendiente: { label: "Pendiente", cls: "bg-amber-100 text-amber-800" },
  aprobada: { label: "Aprobada", cls: "bg-sky-100 text-sky-800" },
  rechazada: { label: "Rechazada", cls: "bg-red-100 text-red-700" },
};
const LABEL = "text-xs font-medium uppercase tracking-wider text-slate-500";

const iso = (y, m, d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

function MonthGrid({ year, month, vacations, holidays, onPick }) {
  const startOffset = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [...Array(startOffset).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const todayIso = new Date().toISOString().slice(0, 10);
  const holidayMap = Object.fromEntries((holidays || []).map((h) => [h.date, h.name]));

  return (
    <div data-testid="vacation-calendar">
      <div className="grid grid-cols-7 gap-1.5">
        {DAY_NAMES.map((d) => (
          <div key={d} className="pb-1 text-center text-[11px] font-medium uppercase tracking-wider text-slate-400">{d}</div>
        ))}
        {cells.map((day, i) => {
          if (!day) return <div key={`e${i}`} />;
          const d = iso(year, month, day);
          const items = vacations.filter((v) => v.start_date <= d && d <= v.end_date);
          const approved = items.some((v) => v.status === "aprobada");
          const pendingDay = !approved && items.some((v) => v.status === "pendiente");
          const holiday = holidayMap[d];
          const clash = new Set(items.map((v) => v.employee_id)).size > 1;
          const tone = approved
            ? "bg-sky-50 border-sky-200"
            : pendingDay ? "bg-amber-50 border-amber-200"
            : holiday ? "bg-slate-100 border-slate-200" : "border-transparent hover:border-slate-200";
          return (
            <button key={day} type="button" onClick={() => onPick?.(d)} disabled={!onPick} title={holiday || undefined}
              className={`min-h-[64px] rounded-lg border p-1.5 text-left align-top transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 ${tone} ${clash ? "ring-1 ring-amber-400" : ""} ${onPick ? "cursor-pointer" : "cursor-default"}`}
              data-testid={`vacation-day-${day}`}>
              <span className={`text-xs font-semibold ${d === todayIso ? "text-accent" : holiday ? "text-slate-400" : "text-slate-600"}`}>{day}</span>
              {holiday && <span className="mt-0.5 block truncate text-[10px] leading-tight text-slate-400">{holiday}</span>}
              <span className="mt-0.5 block space-y-0.5">
                {items.slice(0, 2).map((v) => (
                  <span key={v.id} className={`block truncate text-[11px] leading-tight ${v.status === "pendiente" ? "text-amber-700" : "text-sky-800"}`}>
                    {v.employee_name.split(" ")[0]}
                  </span>
                ))}
                {items.length > 2 && <span className="block text-[11px] text-slate-400">+{items.length - 2}</span>}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function Vacations() {
  const { isAdmin } = useAuth();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [vacations, setVacations] = useState([]);
  const [balance, setBalance] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({ employee_id: "", start_date: "", end_date: "", note: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get(`/vacations?year=${year}`).then((r) => setVacations(r.data)).catch(() => {});
    api.get(`/vacations/balance?year=${year}`).then((r) => setBalance(r.data)).catch(() => {});
    api.get(`/holidays?year=${year}`).then((r) => setHolidays(r.data)).catch(() => {});
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/vacations/pdf?year=${year}`, { responseType: "blob" });
      const m = (res.headers["content-disposition"] || "").match(/filename="?([^"]+)"?/);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = m ? m[1] : `vacaciones_${year}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo generar el PDF"); }
  };
  useEffect(load, [year]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/employees").then((r) => setEmployees(r.data.filter((e) => e.active !== false))).catch(() => {}); }, []);

  const monthVacations = useMemo(() => {
    const mStart = iso(year, month, 1);
    const mEnd = iso(year, month, new Date(year, month + 1, 0).getDate());
    return vacations.filter((v) => v.start_date <= mEnd && v.end_date >= mStart);
  }, [vacations, year, month]);

  const pending = vacations.filter((v) => v.status === "pendiente");
  const approved = vacations.filter((v) => v.status === "aprobada");

  const shift = (n) => {
    const d = new Date(year, month + n, 1);
    setYear(d.getFullYear()); setMonth(d.getMonth());
  };

  const create = async () => {
    if (!form.employee_id) return toast.error("Elige un empleado");
    if (!form.start_date || !form.end_date) return toast.error("Indica fecha de inicio y fin");
    setBusy(true);
    try {
      await api.post("/vacations", form);
      toast.success("Periodo de vacaciones guardado");
      setForm({ employee_id: "", start_date: "", end_date: "", note: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const decide = async (v, status) => {
    try {
      await api.put(`/vacations/${v.id}`, { status });
      toast.success(status === "aprobada" ? "Solicitud aprobada" : "Solicitud rechazada");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (v) => {
    if (!window.confirm(`¿Eliminar las vacaciones de ${v.employee_name} (${fmtDate(v.start_date)} → ${fmtDate(v.end_date)})?`)) return;
    try { await api.delete(`/vacations/${v.id}`); toast.success("Periodo eliminado"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-8" data-testid="vacations-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Vacaciones</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {approved.length === 1 ? "1 periodo aprobado" : `${approved.length} periodos aprobados`} en {year}
            {pending.length > 0 && <> · <span className="text-amber-700 font-medium">{pending.length} pendientes de aprobar</span></>}
          </p>
        </div>
        <Button variant="outline" onClick={downloadPdf} data-testid="vacation-pdf-button">
          <FileDown className="h-4 w-4 mr-2" /> Resumen anual PDF
        </Button>
      </header>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="p-5 sm:p-6 lg:col-span-2">
          <div className="mb-5 flex items-center justify-between">
            <h2 className="font-display text-base md:text-lg font-bold tracking-tight" data-testid="vacation-month-label">
              {MONTH_NAMES[month]} {year}
            </h2>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => shift(-1)} aria-label="Mes anterior" data-testid="vacation-prev-month">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => shift(1)} aria-label="Mes siguiente" data-testid="vacation-next-month">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <MonthGrid year={year} month={month} vacations={monthVacations} holidays={holidays}
            onPick={isAdmin ? (d) => setForm((f) => ({ ...f, start_date: d, end_date: f.end_date && f.end_date >= d ? f.end_date : d })) : undefined} />
          <div className="mt-5 flex flex-wrap gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-sky-100 border border-sky-200" /> Aprobadas</span>
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-amber-50 border border-amber-200" /> Pendientes</span>
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-slate-100 border border-slate-200" /> Festivo</span>
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm ring-1 ring-amber-400" /> Coinciden varios</span>
          </div>
        </Card>

        <div className="space-y-6">
          {isAdmin && (
            <Card className="p-5 sm:p-6" data-testid="vacation-form">
              <h2 className="font-display text-base md:text-lg font-bold tracking-tight">Añadir periodo</h2>
              <p className="mt-1 text-xs text-muted-foreground">Toca un día del calendario para fijar el inicio.</p>
              <div className="mt-5 space-y-4">
                <div className="space-y-1.5">
                  <Label className={LABEL}>Empleado</Label>
                  <select className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm transition-colors hover:border-accent/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
                    value={form.employee_id} onChange={(e) => setForm((f) => ({ ...f, employee_id: e.target.value }))}
                    data-testid="vacation-employee-select">
                    <option value="">Selecciona…</option>
                    {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className={LABEL}>Desde</Label>
                    <Input type="date" value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
                      data-testid="vacation-start-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className={LABEL}>Hasta</Label>
                    <Input type="date" value={form.end_date} onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
                      data-testid="vacation-end-input" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className={LABEL}>Nota</Label>
                  <Input value={form.note} placeholder="Opcional" onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} />
                </div>
                <Button className="w-full" onClick={create} disabled={busy} data-testid="vacation-save-button">
                  {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Guardar periodo
                </Button>
              </div>
            </Card>
          )}

          <Card className="p-5 sm:p-6" data-testid="vacation-pending-list">
            <h2 className="font-display text-base md:text-lg font-bold tracking-tight">Solicitudes pendientes</h2>
            {pending.length === 0 ? (
              <p className="mt-3 text-sm text-muted-foreground">No hay solicitudes por revisar.</p>
            ) : (
              <ul className="mt-4 divide-y">
                {pending.map((v) => (
                  <li key={v.id} className="py-3 first:pt-0 last:pb-0" data-testid={`vacation-request-${v.id}`}>
                    <p className="truncate text-sm font-medium">{v.employee_name}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      <span className="font-mono">{fmtDate(v.start_date)} → {fmtDate(v.end_date)}</span> · {v.days} días
                    </p>
                    {v.note && <p className="mt-1 text-xs italic text-muted-foreground">{v.note}</p>}
                    {(() => {
                      const clash = vacations.filter((o) => o.id !== v.id && o.employee_id !== v.employee_id
                        && o.status !== "rechazada" && o.start_date <= v.end_date && o.end_date >= v.start_date);
                      return clash.length ? (
                        <p className="mt-1.5 flex items-start gap-1.5 text-xs text-amber-700" data-testid={`vacation-clash-${v.id}`}>
                          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                          <span>Coincide con {clash.map((c) => c.employee_name.split(" ")[0]).join(", ")}</span>
                        </p>
                      ) : null;
                    })()}
                    {isAdmin && (
                      <div className="mt-2.5 flex gap-2">
                        <Button size="sm" className="h-8 text-xs" onClick={() => decide(v, "aprobada")} data-testid={`approve-vacation-${v.id}`}>
                          <Check className="h-3.5 w-3.5 mr-1.5" /> Aprobar
                        </Button>
                        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => decide(v, "rechazada")} data-testid={`reject-vacation-${v.id}`}>
                          <X className="h-3.5 w-3.5 mr-1.5" /> Rechazar
                        </Button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>

      <Card className="p-5 sm:p-6" data-testid="vacation-balance">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="font-display text-base md:text-lg font-bold tracking-tight">Saldo de {year}</h2>
          <p className="text-xs text-muted-foreground">
            {balance ? `${balance.quota} días naturales por trabajador` : ""} · configurable en Ajustes
          </p>
        </div>
        {!balance || balance.rows.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">Sin empleados activos.</p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className={`py-2 pr-4 ${LABEL}`}>Trabajador</th>
                  <th className={`py-2 pr-4 ${LABEL}`}>Consumidos</th>
                  <th className={`py-2 pr-4 ${LABEL}`}>Pendientes</th>
                  <th className={`py-2 pr-4 ${LABEL}`}>Disponibles</th>
                  <th className={`py-2 ${LABEL}`}>Consumo</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {balance.rows.map((r) => {
                  const pct = Math.min(100, Math.round((r.used / (r.quota || 1)) * 100));
                  return (
                    <tr key={r.employee_id} className="transition-colors hover:bg-slate-50/80" data-testid={`balance-row-${r.employee_id}`}>
                      <td className="py-3 pr-4 font-medium">{r.employee_name}</td>
                      <td className="py-3 pr-4">{r.used}</td>
                      <td className="py-3 pr-4 text-amber-700">{r.pending || "—"}</td>
                      <td className={`py-3 pr-4 font-semibold ${r.remaining < 0 ? "text-red-600" : ""}`}>{r.remaining}</td>
                      <td className="py-3 w-40">
                        <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                          <div className={`h-full ${pct >= 100 ? "bg-red-500" : "bg-accent"}`} style={{ width: `${pct}%` }} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card className="p-5 sm:p-6" data-testid="vacation-year-list">
        <h2 className="font-display text-base md:text-lg font-bold tracking-tight">Periodos de {year}</h2>
        {vacations.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">Sin vacaciones registradas este año.</p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className={`py-2 pr-4 ${LABEL}`}>Empleado</th>
                  <th className={`py-2 pr-4 ${LABEL}`}>Desde</th>
                  <th className={`py-2 pr-4 ${LABEL}`}>Hasta</th>
                  <th className={`py-2 pr-4 ${LABEL}`}>Días</th>
                  <th className={`py-2 pr-4 ${LABEL}`}>Estado</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody className="divide-y">
                {vacations.map((v) => (
                  <tr key={v.id} className="transition-colors hover:bg-slate-50/80" data-testid={`vacation-row-${v.id}`}>
                    <td className="py-3 pr-4 font-medium">{v.employee_name}</td>
                    <td className="py-3 pr-4 font-mono text-xs text-muted-foreground">{fmtDate(v.start_date)}</td>
                    <td className="py-3 pr-4 font-mono text-xs text-muted-foreground">{fmtDate(v.end_date)}</td>
                    <td className="py-3 pr-4">{v.days}</td>
                    <td className="py-3 pr-4">
                      <Badge className={`${STATUS[v.status]?.cls} text-[11px] font-medium`}>{STATUS[v.status]?.label || v.status}</Badge>
                    </td>
                    <td className="py-3 text-right">
                      {isAdmin && (
                        <button onClick={() => remove(v)} aria-label="Eliminar periodo"
                          className="rounded p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-200"
                          data-testid={`delete-vacation-${v.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
